import { Table } from "antd";
import { useEffect } from "react";
import styles from "./index.module.scss";
import {
  renderMonthlySummaryCell,
  renderDate,
  renderMonthlySummaryCellPercent,
} from "../utils";
import { useState } from "react";
import * as echarts from "echarts~5";
import moment from "moment";

const useResizeEchart = (dom) => {
  useEffect(() => {
    if (dom) {
      const resizeObserver = new ResizeObserver(() => {
        let instance = echarts.getInstanceByDom(dom);
        if (instance) {
          instance.resize();
        }
      });
      resizeObserver.observe(dom);
      return () => {
        resizeObserver.unobserve(dom);
      };
    }
  }, [dom]);
  return [];
};

const useChart = (monthDataSource, dayDataSource) => {
  const [chartDom, setChartDom] = useState(null);
  useResizeEchart(chartDom);
  const refFunc = (d) => {
    setChartDom(d);
  };
  useEffect(() => {
    if (chartDom) {
      let instance = echarts.getInstanceByDom(chartDom);
      if (!instance) {
        instance = echarts.init(chartDom, null, {
          locale: "ZH",
        });
      } else {
        instance.clear();
      }
      const lastDatetime = monthDataSource?.[0]?.cutoff_date ?? null;

      if (
        typeof lastDatetime === "string" &&
        lastDatetime !== "" &&
        dayDataSource
      ) {
        instance.setOption({
          legend: {
            show: true,
            top: 24,
            textStyle: {
              fontSize: 18,
            },
          },
          tooltip: {
            show: true,
            trigger: "axis",
            formatter: (params) => {
              // console.log(params)
              const axisValueLabel = params?.[0]?.axisValueLabel ?? "--";
              return `${axisValueLabel}<br/>${(params ?? []).map(({ marker, seriesName, data: { actualVal } }) => `${marker} ${seriesName} ${actualVal ?? "--"}`).join("<br/>")}`;
            },
            textStyle: {
              fontSize: 18,
            },
          },
          grid: {
            containLabel: true,
            top: 76,
            right: 24,
            bottom: 24,
            left: 24,
          },
          xAxis: [
            {
              type: "category",
              axisLabel: {
                fontSize: 18,
              },
              data: (dayDataSource ?? []).map(({ originalData }) =>
                moment(originalData.datetime).format("D日"),
              ),
              axisTick: {
                show: false,
              },
            },
          ],
          yAxis: [
            {
              type: "value",
              axisLabel: {
                fontSize: 18,
              },
              interval: 50,
              max: ({ max }) => {
                if (max >= 500) {
                  return 500;
                } else {
                  if (max > 0) {
                    const m = Math.ceil(max / 50) * 50;
                    return m;
                  }
                  return undefined;
                }
              },
            },
          ],
          series: [
            {
              name: "月累计电价",
              type: "bar",
              barWidth: "50%",
              data: (dayDataSource ?? []).map((item) => {
                const { originalData, cum_actual_price } = item;
                let value = null;
                if (
                  moment(originalData.datetime).isSameOrBefore(
                    moment(lastDatetime),
                  )
                ) {
                  value = renderMonthlySummaryCell(cum_actual_price);
                } else {
                }
                let pVal = value;
                if (typeof pVal === "number" && !isNaN(pVal)) {
                  if (pVal < 0) {
                    pVal = 0;
                  } else if (pVal > 500) {
                    pVal = 500;
                  }
                }
                return {
                  value: pVal,
                  actualVal: value,
                };
              }),
              itemStyle: {
                color: "#6887B9",
              },
            },
            {
              name: "每日实际电价",
              type: "line",
              data: (dayDataSource ?? []).map((item) => {
                const { originalData, actual_price } = item;
                let value = null;
                if (
                  moment(originalData.datetime).isSameOrBefore(
                    moment(lastDatetime),
                  )
                ) {
                  value = renderMonthlySummaryCell(actual_price);
                } else {
                }
                let pVal = value;
                if (typeof pVal === "number" && !isNaN(pVal)) {
                  if (pVal < 0) {
                    pVal = 0;
                  } else if (pVal > 500) {
                    pVal = 500;
                  }
                }
                return {
                  value: pVal,
                  actualVal: value,
                  label: {
                    show: true,
                    fontSize: 18,
                    formatter: () => value,
                  },
                };
              }),
              itemStyle: {
                color: "#DF7F7F",
              },
              lineStyle: {
                width: 4,
              },
              symbolSize: 8,
            },
            {
              name: "合约电价",
              type: "line",
              data: (dayDataSource ?? []).map((item) => {
                const { contract_price } = item;
                const value = renderMonthlySummaryCell(contract_price);
                let pVal = value;
                if (typeof pVal === "number" && !isNaN(pVal)) {
                  if (pVal < 0) {
                    pVal = 0;
                  } else if (pVal > 500) {
                    pVal = 500;
                  }
                }
                return {
                  value: pVal,
                  actualVal: value,
                };
              }),
              itemStyle: {
                color: "#C0B2D0",
              },
              lineStyle: {
                type: "dashed",
                width: 4,
              },
              showSymbol: false,
            },
          ],
        });
      }
    }
  }, [monthDataSource, dayDataSource, chartDom]);
  return [refFunc];
};

const Comp = (props) => {
  const { monthDataSource, dayDataSource, loading1, loading2 } = props;
  const [refFunc] = useChart(monthDataSource, dayDataSource);
  return (
    <div className={styles.wrapper}>
      <div className="top" ref={refFunc}></div>
      <div className="bottom">
        <Table
          size="small"
          pagination={false}
          bordered
          loading={loading2 || loading1}
          dataSource={monthDataSource ?? []}
          columns={[
            {
              title: "统计截止日期",
              dataIndex: "cutoff_date",
              render: renderDate,
              key: "cutoff_date",
            },
            {
              title: (
                <span>
                  合约电量 <br /> (MWH)
                </span>
              ),
              dataIndex: "contract_mwh",
              key: "contract_mwh",
            },
            {
              title: (
                <span>
                  累计电量 <br /> (MWH)
                </span>
              ),
              dataIndex: "cum_actual_mwh",
              key: "cum_actual_mwh",
            },
            {
              title: (
                <span>
                  超额/缺额
                  <br />
                  (%)
                </span>
              ),
              dataIndex: "cum_deviation_perc",
              key: "cum_deviation_perc",
            },
            {
              title: (
                <span>
                  超额/缺额
                  <br />
                  (MWH)
                </span>
              ),
              dataIndex: "cum_deviation_NOValue",
              key: "cum_deviation_NOValue",
            },
            {
              title: (
                <span>
                  合约电价 <br /> (元/MWH)
                </span>
              ),
              dataIndex: "contract_price",
              key: "contract_price",
            },
            {
              title: (
                <span>
                  累计电价 <br /> (元/MWH)
                </span>
              ),
              dataIndex: "cum_actual_price",
              key: "cum_actual_price",
            },
            {
              title: (
                <span>
                  平均现货电价 <br /> (元/MWH)
                </span>
              ),
              dataIndex: "d0_spot_price",
              key: "d0_spot_price",
            },
            {
              title: (
                <span>
                  收益 <br /> (元)
                </span>
              ),
              dataIndex: "cum_actual_profit",
              key: "cum_actual_profit",
            },
          ].map((item) => ({ ...item, align: "center" }))}
          onHeaderRow={() => ({
            className: styles.headerRow,
          })}
        />
      </div>
    </div>
  );
};

export default Comp;
